// Order tracking utilities for no-backend client-side tracking.
// Orders can be tracked two ways:
//   1. localStorage (works on the device that placed the order)
//   2. Shareable URL with a base64-encoded order payload (works anywhere)
// Status progression is derived from elapsed time + scheduled delivery date,
// so the page always shows a believable, evolving status without a server.

export interface TrackedOrderItem {
  product: string
  quantity: number
  pricePerYard: number
}

export interface TrackedOrder {
  orderId: string
  createdAt: number // ms since epoch
  address: string
  name?: string
  phone?: string
  email?: string
  date: string // ISO date string for scheduled delivery
  time?: string // human-readable window e.g. "9:00 AM - 12:00 PM"
  instructions?: string
  items: TrackedOrderItem[]
  totalDeliveryFee: number
  estimatedTax: number
  finalTotal: number
}

const STORAGE_KEY = '253gravel_tracked_orders'
const LATEST_KEY = '253gravel_latest_order_id'

// ---------- Persistence ----------

function readAll(): Record<string, TrackedOrder> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function writeAll(orders: Record<string, TrackedOrder>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders))
  } catch {
    /* ignore */
  }
}

export function saveOrderForTracking(order: TrackedOrder) {
  if (!order?.orderId) return
  const all = readAll()
  all[order.orderId] = order
  writeAll(all)
  try {
    localStorage.setItem(LATEST_KEY, order.orderId)
  } catch {
    /* ignore */
  }
}

export function loadOrderById(orderId: string): TrackedOrder | null {
  if (!orderId) return null
  const all = readAll()
  return all[orderId] || null
}

export function loadLatestOrder(): TrackedOrder | null {
  if (typeof window === 'undefined') return null
  try {
    const id = localStorage.getItem(LATEST_KEY)
    if (!id) return null
    return loadOrderById(id)
  } catch {
    return null
  }
}

// ---------- Share-link token ----------

// Base64-url-safe encoding (works with unicode characters)
function b64UrlEncode(str: string): string {
  const utf8 = unescape(encodeURIComponent(str))
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64UrlDecode(token: string): string {
  const padded = token.replace(/-/g, '+').replace(/_/g, '/')
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4))
  const decoded = atob(padded + pad)
  return decodeURIComponent(escape(decoded))
}

export function encodeOrderToken(order: TrackedOrder): string {
  try {
    return b64UrlEncode(JSON.stringify(order))
  } catch {
    return ''
  }
}

export function decodeOrderToken(token: string): TrackedOrder | null {
  if (!token) return null
  try {
    const json = b64UrlDecode(token)
    const parsed = JSON.parse(json) as TrackedOrder
    if (!parsed?.orderId) return null
    return parsed
  } catch {
    return null
  }
}

export function buildShareLink(order: TrackedOrder): string {
  const token = encodeOrderToken(order)
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://253gravel.com'
  return `${origin}/#/track/${encodeURIComponent(order.orderId)}?d=${token}`
}

// ---------- Status derivation ----------

export type OrderStepId =
  | 'confirmed'
  | 'scheduled'
  | 'dispatched'
  | 'en_route'
  | 'delivered'

export const STEP_ORDER: OrderStepId[] = [
  'confirmed',
  'scheduled',
  'dispatched',
  'en_route',
  'delivered',
]

// Returns the current step index (0-4) given the order data and current time.
// Logic:
//   - Right after placement (< 2 min): "confirmed"
//   - Before delivery day:               "scheduled"
//   - On delivery day, before window:    "dispatched"
//   - During delivery window:            "en_route"
//   - After window ends:                 "delivered"
// If delivery date is missing/unparseable, falls back to elapsed time.
export function getCurrentStepIndex(
  order: TrackedOrder,
  now: number = Date.now(),
): number {
  const elapsedMs = now - (order.createdAt || now)

  // Try to parse delivery date + window
  const deliveryStart = parseDeliveryWindowStart(order.date, order.time)
  const deliveryEnd = parseDeliveryWindowEnd(order.date, order.time)

  // First 2 minutes: confirmed
  if (elapsedMs < 2 * 60 * 1000) return 0

  if (!deliveryStart || !deliveryEnd) {
    // Fallback: pure elapsed-time progression for demo / missing-date orders
    if (elapsedMs < 30 * 60 * 1000) return 1 // scheduled (< 30 min)
    if (elapsedMs < 60 * 60 * 1000) return 2 // dispatched (< 1 hr)
    if (elapsedMs < 90 * 60 * 1000) return 3 // en route (< 1.5 hr)
    return 4 // delivered
  }

  // 90 min before window start = dispatched
  if (now < deliveryStart - 90 * 60 * 1000) return 1 // scheduled
  if (now < deliveryStart) return 2 // dispatched
  if (now < deliveryEnd) return 3 // en route
  return 4 // delivered
}

// Parse a YYYY-MM-DD string as a LOCAL-time Date at midnight.
// Using `new Date("2026-05-12")` parses as UTC midnight, which in Pacific
// time becomes May 11 ~4-5pm — causing the delivery window to appear to
// have already passed and prematurely marking orders as "Delivered".
function parseLocalDate(dateStr: string): Date | null {
  // ISO date-only string (YYYY-MM-DD) — construct from local parts
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) {
    const y = parseInt(m[1], 10)
    const mo = parseInt(m[2], 10) - 1
    const d = parseInt(m[3], 10)
    const dt = new Date(y, mo, d, 0, 0, 0, 0)
    return isNaN(dt.getTime()) ? null : dt
  }
  // Fallback for full datetime strings — trust the parser
  const dt = new Date(dateStr)
  return isNaN(dt.getTime()) ? null : dt
}

// Try to parse the delivery window start from order.date + order.time.
// order.time looks like "9:00 AM - 12:00 PM" or "Any time".
function parseDeliveryWindowStart(
  dateStr?: string,
  timeStr?: string,
): number | null {
  if (!dateStr) return null
  const day = parseLocalDate(dateStr)
  if (!day) return null
  if (!timeStr || /any/i.test(timeStr)) {
    // Default 8 AM if no window
    day.setHours(8, 0, 0, 0)
    return day.getTime()
  }
  const startToken = timeStr.split(/[-–—]/)[0]?.trim()
  const parsed = parseClockTime(startToken)
  if (!parsed) {
    day.setHours(8, 0, 0, 0)
    return day.getTime()
  }
  day.setHours(parsed.hours, parsed.minutes, 0, 0)
  return day.getTime()
}

function parseDeliveryWindowEnd(
  dateStr?: string,
  timeStr?: string,
): number | null {
  if (!dateStr) return null
  const day = parseLocalDate(dateStr)
  if (!day) return null
  if (!timeStr || /any/i.test(timeStr)) {
    day.setHours(18, 0, 0, 0)
    return day.getTime()
  }
  const parts = timeStr.split(/[-–—]/)
  const endToken = (parts[1] || parts[0])?.trim()
  const parsed = parseClockTime(endToken)
  if (!parsed) {
    day.setHours(18, 0, 0, 0)
    return day.getTime()
  }
  day.setHours(parsed.hours, parsed.minutes, 0, 0)
  return day.getTime()
}

function parseClockTime(
  token?: string,
): { hours: number; minutes: number } | null {
  if (!token) return null
  const m = token.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const min = m[2] ? parseInt(m[2], 10) : 0
  const ampm = m[3]?.toLowerCase()
  if (ampm === 'pm' && h < 12) h += 12
  if (ampm === 'am' && h === 12) h = 0
  return { hours: h, minutes: min }
}

// Best-effort ETA string for the live ETA badge.
export function getEtaLabel(
  order: TrackedOrder,
  now: number = Date.now(),
): string {
  const step = getCurrentStepIndex(order, now)
  if (step === 4) return 'Delivered'
  const end = parseDeliveryWindowEnd(order.date, order.time)
  if (!end) {
    if (step === 0) return 'Just placed'
    if (step === 1) return 'Scheduled'
    if (step === 2) return '~45 min'
    return '~15 min'
  }
  const diff = end - now
  if (diff <= 0) return 'Arriving now'
  const hours = Math.floor(diff / (60 * 60 * 1000))
  const mins = Math.round((diff % (60 * 60 * 1000)) / (60 * 1000))
  if (hours > 24) return `${Math.round(hours / 24)} day(s)`
  if (hours >= 1) return `~${hours}h ${mins}m`
  return `~${mins} min`
}
